import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
} from "@sellar-io/components";
import {
  OrderGetResponseType,
  SupplierRelationshipListResponseType,
} from "@sellar-io/api-v2/types";
import {
  calculateItemTotal,
  calculateVatTotal,
  formatCurrency,
} from "@sellar-io/common";
import React, { ReactElement, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { CUSTOMER } from "../../../constants/routes";
import { useNavigateToChild } from "../../../hooks/useParentNavigation";
import {
  getOrderStage,
  OrderSummaryItemList,
  OrderSummaryPrice,
  PaidBadge,
} from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { FullDutyBadge } from "../../../components/badge/FullDutyBadge";
import { RequestedCreditTermsAlert } from "../../../components/connectionPaymentTermsRequest/RequestedCreditTermsAlert";
import { OpeningOrderFeeLabel } from "../../../components/openingOrderFee/OpeningOrderFeeLabel";
import { PaymentTermsIndicator } from "../../../components/paymentTypeIndicator/PaymentTermsIndicator";
import { usePriceListList } from "../../../hooks/api/v2/priceList/usePriceListList";
import { useSupplierRelationshipList } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { useSupplierRelationshipUpdate } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipUpdate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { renderTaxName } from "../../../utils/sellarHelpers";
import { EditPaymentTermsRadioSelector } from "../../customer/businessConnection/connectionSelector/EditPaymentTermsRadioSelector";
import OrderFulfillmentIndicator from "../orderFulfillmentIndicator/OrderFulfillmentIndicator";
import { OrderInfoCard } from "../OrderInforCard/OrderInfoCard";
import { SharedOrderCards } from "../SharedOrderCards";
import { RequestedOrderDropdownList } from "./RequestedOrderDropdownList";
import { ConsolidatedBanner } from "../../../components/consolidatedBanner/ConsolidateBanner";
import {
  BadgeGreen,
  BadgeWhite,
  BadgePurple,
} from "../../../components/badge/Badge";
import { SellarCreditPaymentTypeIndicator } from "../../../components/paymentTypeIndicator/SellarCreditPaymentTypeIndicator";
import { RequestedOrderPaymentLink } from "./RequestedOrderPaymentLink/RequestedOrderPaymentLink";
import { DepositPaidBadge } from "../../../components/badge/DepositPaid";
import { useSellarCreditPaymentStatus } from "~/components/newOrderSummary/useSellarCreditPaymentStatus";

export function RequestedOrderContent({
  order,
  setShowPaymentLinkPreview,
  orderUsesSDPalletShipment,
}: {
  order: OrderGetResponseType;
  setShowPaymentLinkPreview: (set: boolean) => void;
  orderUsesSDPalletShipment: boolean;
}): ReactElement | null {
  const { business } = useSellarCurrentBusiness();
  const navigateToChild = useNavigateToChild();
  const supplierRelationshipQuery = useSupplierRelationshipList(
    {
      query: [
        {
          type: "buyerId",
          modifier: "EQUALS",
          value: order.RetailerId,
        },
      ],
      limit: 1,
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!order,
    },
  );
  const showSellarCreditFeatures = order.paymentType === "sellarCredit";

  const priceTiersQuery = usePriceListList({});
  const connection = supplierRelationshipQuery.data?.pages[0]?.[0];

  const [paymentDueCalculateFrom, setPaymentDueCalculateFrom] = useState<
    SupplierRelationshipListResponseType[number]["paymentDueCalculateFrom"]
  >(connection?.paymentDueCalculateFrom ?? null);

  const [paymentTerms, setPaymentTerms] = useState<
    SupplierRelationshipListResponseType[number]["paymentTerms"]
  >(connection?.paymentTerms ?? "proforma");
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [paymentDue, setPaymentDue] = useState<number | null>(
    connection?.paymentDue ?? null,
  );
  const [error, setError] = useState<string>();
  const [paymentTermsActionedMessage, setPaymentTermsActionedMessage] =
    useState<string>();
  const [previousConnection, setPreviousConnection] = useState(connection);
  if (connection !== previousConnection) {
    setPreviousConnection(connection);
    setPaymentTerms(connection?.paymentTerms ?? "proforma");
    setPaymentDueCalculateFrom(connection?.paymentDueCalculateFrom ?? null);
    setPaymentDue(connection?.paymentDue ?? null);
  }
  const isNewCustomer = Boolean(connection && !connection.approved);
  const supplierRelationshipUpdateMutate = useSupplierRelationshipUpdate(
    connection?.id ?? 0,
  );
  const { isProcessed: isSellarCreditOrderProcessed } =
    useSellarCreditPaymentStatus({
      orderId: order.id,
    });
  const isLoading = supplierRelationshipUpdateMutate.isPending;

  const canShowSellarCreditOrderNotPaidActionPanel =
    !isSellarCreditOrderProcessed && order.paymentType === "sellarCredit";

  const defaultPriceTier = priceTiersQuery.data?.pages[0]?.find(
    (priceTier) => priceTier.id === order.PriceTierId,
  );
  const defaultPriceTierName = defaultPriceTier?.name;

  const hasPaymentTermsChanged = paymentTerms !== connection?.paymentTerms;
  const hasPaymentDueCalcFromChanged =
    connection?.paymentDueCalculateFrom !== paymentDueCalculateFrom;
  const hasPaymentDueChanged = connection?.paymentDue !== paymentDue;

  const hasConnectionBeenEdited =
    hasPaymentTermsChanged ||
    hasPaymentDueChanged ||
    hasPaymentDueCalcFromChanged;

  const hasOrderDepositBeenPaid = order.PaymentList.some(
    (payment) => payment.type === "cardDeposit" && payment.status === "paid",
  );

  const onSubmit = () => {
    setError("");
    if (!paymentTerms || !connection) return;
    if (paymentTerms === "credit" && !paymentDue)
      return setError("Number of days credit is required");

    if (paymentDue && paymentDue < 0) {
      setError("Number of days credit must be greater than or equal to 0");
      return;
    }

    return supplierRelationshipUpdateMutate.mutate({
      paymentDue,
      paymentTerms,
      paymentDueCalculateFrom,
    });
  };

  const isCompleteForm = paymentTerms === "credit" && !paymentDue;
  const hasRequestedPaymentTerms =
    connection?.PaymentTermsRequest?.status === "pending";

  const requestedPaymentTerms = hasRequestedPaymentTerms
    ? connection.PaymentTermsRequest
    : null;

  const openingOrderFee = order.OpeningOrderFee;

  const orderStage = getOrderStage(order);

  const sellarCreditOrderHasBeenAddedToTab =
    order.TwoOrder?.verificationStatus === "verified" &&
    order.TwoOrder.state === "approved";

  const showAwaitingConfirmation =
    !!business?.Supplier &&
    order.paymentType === "sellarCredit" &&
    order.initiator === "supplier" &&
    !sellarCreditOrderHasBeenAddedToTab;

  const buyerVenueName = order.Retailer.Business.name;

  return (
    <>
      {paymentTermsActionedMessage && (
        <Alert
          className={"w-full"}
          title={paymentTermsActionedMessage}
          message={""}
          type={"success"}
        />
      )}
      {!!requestedPaymentTerms &&
        !paymentTermsActionedMessage &&
        connection && (
          <RequestedCreditTermsAlert
            requestedPaymentTerms={requestedPaymentTerms}
            connection={connection}
            buyerName={order.Retailer.Business.name}
            onSuccess={setPaymentTermsActionedMessage}
            orderId={order.id}
          />
        )}

      {showAwaitingConfirmation && (
        <ConsolidatedBanner
          title={
            isDesktop
              ? "Order requires buyer confirmation"
              : "Buyer confirmation"
          }
          description={
            <p>
              {order.Retailer.Business.name} have been notified of this order.
              They must confirm the order request to add it to their Sellar
              Credit tab before further action can be taken. Alternatively,
              share a link with this customer where they can add the order to
              their tab. They will be asked to login to their account.
            </p>
          }
          badge={
            <BadgeWhite
              icon={"CLOCK_OUTLINE"}
              className={"border border-zinc-300"}
            >
              Awaiting confirmation
            </BadgeWhite>
          }
        />
      )}
      {isNewCustomer && !openingOrderFee && (
        <ConsolidatedBanner
          title={"First time order!"}
          description={
            <p>
              {order.Retailer.Business.name} have placed an order after
              following your link: <b>{defaultPriceTierName}</b>. Accepting
              their order will add them to your customer list, with default
              terms and pricing.
            </p>
          }
          badge={
            <BadgeGreen
              icon="LINK"
              className="border border-emerald-300 font-normal text-emerald-800"
            >
              From your link
            </BadgeGreen>
          }
        />
      )}
      {!!openingOrderFee && (
        <ConsolidatedBanner
          title={"First time order!"}
          description={
            <p>
              {order.Retailer.Business.name} have found you on the{" "}
              <b>Sellar Market</b> and have placed an order with you for the
              first time. Accepting their order will add them to your customer
              list, with default terms and pricing.
            </p>
          }
          badge={
            <BadgePurple
              icon="SELLAR"
              className="border border-purple-300 font-normal text-purple-800"
            >
              From Sellar Market
            </BadgePurple>
          }
        >
          <OpeningOrderFeeLabel fullLabel={true} fee={openingOrderFee} />
        </ConsolidatedBanner>
      )}
      {showAwaitingConfirmation && (
        <RequestedOrderPaymentLink
          orderId={order.id}
          setShowPaymentLinkPreview={setShowPaymentLinkPreview}
        />
      )}

      <Card className={twMerge("bg-white", "flex flex-col gap-4 p-4")}>
        {canShowSellarCreditOrderNotPaidActionPanel && (
          <SellarCreditOrderNotPaidAlert buyerVenueName={buyerVenueName} />
        )}
        <div className="flex justify-end">
          <RequestedOrderDropdownList order={order} />
        </div>

        <OrderInfoCard
          total={order.price}
          businessName={order.Retailer.Business.name}
          logoUrl={order.Retailer.Business.logoUrl}
          isNewCustomer={!!connection && !connection.approved}
          onBusinessNameClicked={() =>
            connection &&
            navigateToChild(CUSTOMER.href({ connectionId: connection.id }))
          }
        >
          <div className="flex flex-wrap items-center justify-center gap-1">
            <OrderFulfillmentIndicator
              fulfillmentType={order.fulfillmentType}
            />
            {showSellarCreditFeatures && (
              <SellarCreditPaymentTypeIndicator
                canAccessTab={false}
                orderStage={orderStage}
                userType="supplier"
              />
            )}
            {!showSellarCreditFeatures && (
              <PaymentTermsIndicator
                connection={connection}
                invoiceCreationStage={order.Supplier.invoiceCreationStage}
              />
            )}
            {order.paymentStatus === "paid" && <PaidBadge />}
            {hasOrderDepositBeenPaid && <DepositPaidBadge />}
            {order.hasNoDraughtDutyRelief && (
              <FullDutyBadge className={"gap-0"} />
            )}
            {showAwaitingConfirmation && (
              <BadgeWhite
                icon={"CLOCK_OUTLINE"}
                className={"border border-zinc-300"}
              >
                Awaiting confirmation
              </BadgeWhite>
            )}
          </div>
        </OrderInfoCard>
        {!showAwaitingConfirmation && (
          <>
            {orderUsesSDPalletShipment ? (
              <div className="flex flex-col items-center gap-4">
                <p className={"text-center font-medium"}>
                  By accepting this order you acknowledge that it will be
                  delivered by Sellar Distribution. Once accepted, the order
                  will be finalised on your behalf according to the pallet
                  shipment schedule.
                </p>
                <Alert
                  type="information"
                  title="We will notify you by email when it’s time to get this SD Pallet order ready to ship"
                  message=""
                ></Alert>
              </div>
            ) : (
              <p className={"text-center font-medium"}>
                Accept this order to let {order.Retailer.Business.name} know
                that you intend to fulfil their request. You will still be able
                to edit this order and its fulfilment details after accepting.
              </p>
            )}
          </>
        )}
        <div className="mt-4 flex flex-col gap-2">
          <OrderSummaryItemList
            items={order.OrderItems.map((orderItem) => ({
              description: orderItem.Product.name ?? "",
              quantity: orderItem.quantity,
              cost: formatCurrency(orderItem.price),
              sku: orderItem.Product.sku,
              fullPrice: orderItem.fullPrice,
              price: orderItem.price,
              promotionAppliedAt: orderItem.promotionAppliedAt,
            }))}
          />
          <OrderSummaryPrice
            delivery={formatCurrency(order.deliveryCharge, order.currency)}
            subTotal={formatCurrency(
              calculateItemTotal(order.OrderItems),
              order.currency,
            )}
            isDeliveryChargeCapped={order.deliveryChargeCapped}
            discount={
              order.discountAmount > 0
                ? {
                    value: formatCurrency(order.discountAmount, order.currency),
                    calculatedTotal: formatCurrency(
                      order.discountAmount,
                      order.currency,
                    ),
                  }
                : undefined
            }
            tax={{
              name: renderTaxName(order.currency),
              percentage: order.vatPercentage.toString(),
              total: formatCurrency(
                calculateVatTotal({ ...order, orderItems: order.OrderItems }),
                order.currency,
              ),
            }}
            total={formatCurrency(order.price, order.currency)}
          />
        </div>
      </Card>
      {!showSellarCreditFeatures && (
        <Card className="bg-white">
          <CardHeader
            className={twMerge(
              "bg-white",
              "flex items-center justify-between p-4",
            )}
          >
            <H5>Payment terms</H5>
          </CardHeader>
          {error && (
            <Alert message={error} type={"error"} className={"w-full"} />
          )}

          <CardContent className={"fle flex-col gap-2 p-4"}>
            <EditPaymentTermsRadioSelector
              paymentTerms={paymentTerms}
              setPaymentTerms={(paymentTerms) => setPaymentTerms(paymentTerms)}
              setPaymentDueCalculateFrom={(paymentDueCalculateFrom) =>
                setPaymentDueCalculateFrom(paymentDueCalculateFrom)
              }
              paymentDueCalculateFrom={paymentDueCalculateFrom}
              paymentDue={paymentDue}
              setPaymentDue={(paymentDue) => setPaymentDue(paymentDue)}
              invoiceCreationStage={business?.Supplier?.invoiceCreationStage}
            />
            <div className="mt-2 flex justify-end">
              <Button
                loading={isLoading}
                disabled={
                  isLoading || !hasConnectionBeenEdited || isCompleteForm
                }
                size={"small"}
                onClick={onSubmit}
              >
                Update terms
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      <SharedOrderCards
        order={order}
        connection={connection}
        buyer={connection?.Buyer}
      />
    </>
  );
}

const SellarCreditOrderNotPaidAlert = ({
  buyerVenueName,
}: {
  buyerVenueName: string;
}) => {
  return (
    <Alert
      type="information"
      title="Customer payment incomplete"
      message={`You cannot finalise this order yet. ${buyerVenueName} must complete their request to pay using Sellar Credit.`}
    />
  );
};
